import { useState, useMemo } from 'react'
import { useDepartment } from '../context/DepartmentContext'
import { useToast } from '../context/ToastContext'
import { getSalesInRange, getFlags, getDailyCategorySummaryRange } from '../lib/store'
import { formatR, formatPct, calcStats, formatDate } from '../lib/calculations'
import { getCategories } from '../data/categories'
import { BAKERY_CATEGORIES_SEED } from '../data/bakeryData'
import { YAWEHS_CATEGORIES_SEED } from '../data/yawehsData'
import { TUCKSHOP_CATEGORIES_SEED } from '../data/tuckshopData'

function getCategoriesForDept(dept) {
  if (dept === 'yawehs') return YAWEHS_CATEGORIES_SEED
  if (dept === 'bakery') return BAKERY_CATEGORIES_SEED
  if (dept === 'tuckshop') return TUCKSHOP_CATEGORIES_SEED
  return getCategories()
}
import { FileDown, FileText, Calendar, CalendarDays, CalendarRange } from 'lucide-react'
import DateInput from '../components/ui/DateInput'

function getMonthDays(year, month) {
  const start = `${year}-${String(month).padStart(2, '0')}-01`
  const end = new Date(year, month, 0).toISOString().split('T')[0]
  return { start, end }
}

function getWeekRange(dateStr) {
  const d = new Date(dateStr)
  const day = d.getDay()
  const start = new Date(d)
  start.setDate(d.getDate() - day)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] }
}

const REPORT_MODES = [
  { id: 'day', label: 'Day', icon: Calendar },
  { id: 'week', label: 'Week', icon: CalendarDays },
  { id: 'month', label: 'Month', icon: CalendarRange },
]

export default function Reports() {
  const { currentDepartmentId } = useDepartment()
  const { addToast } = useToast()
  const now = new Date()
  const [mode, setMode] = useState('month')
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [selectedDate, setSelectedDate] = useState(now.toISOString().split('T')[0])

  const dateRange = useMemo(() => {
    if (mode === 'day') return { start: selectedDate, end: selectedDate }
    if (mode === 'week') return getWeekRange(selectedDate)
    return getMonthDays(year, month)
  }, [mode, year, month, selectedDate])

  const periodLabel = useMemo(() => {
    if (mode === 'day') return formatDate(selectedDate)
    if (mode === 'week') return `${formatDate(dateRange.start)} — ${formatDate(dateRange.end)}`
    return new Date(year, month - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })
  }, [mode, year, month, selectedDate, dateRange])

  const report = useMemo(() => {
    const dept = currentDepartmentId
    const { start, end } = dateRange

    const sales = getSalesInRange(dept, start, end)
    const flags = getFlags(dept, { startDate: start, endDate: end })
    const catSummaries = getDailyCategorySummaryRange(dept, start, end)

    // Build daily totals from sales (not from dailySummaries which may be empty)
    const dailyTotals = {}
    for (const s of sales) {
      if (!s.date) continue
      if (!dailyTotals[s.date]) dailyTotals[s.date] = 0
      dailyTotals[s.date] += Number(s.salesRands) || 0
    }
    const sortedDates = Object.keys(dailyTotals).sort()
    const dailySales = sortedDates.map(d => dailyTotals[d])
    const stats = calcStats(dailySales.length > 0 ? dailySales : [0])

    const tradingDays = sortedDates.length

    // Build summaries array for PDF export and UI (derived from sales)
    const summaries = sortedDates.map(date => ({
      date,
      totalSales: dailyTotals[date],
      dayOfWeek: new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }),
    }))

    const bestDay = summaries.reduce((best, s) => (!best || s.totalSales > (best.totalSales || 0)) ? s : best, null)
    const worstDay = summaries.filter(s => s.totalSales > 0).reduce((worst, s) => (!worst || s.totalSales < worst.totalSales) ? s : worst, null)

    // Day of week perf
    const dowMap = {}
    for (const s of summaries) {
      const dow = s.dayOfWeek
      if (!dowMap[dow]) dowMap[dow] = { total: 0, count: 0 }
      dowMap[dow].total += s.totalSales || 0
      dowMap[dow].count++
    }
    const dowPerf = Object.entries(dowMap).map(([day, v]) => ({
      day, total: v.total, avg: v.total / v.count, days: v.count,
    }))

    // Waste from category summaries (if available)
    const wasteByCategory = getCategoriesForDept(currentDepartmentId).map(cat => {
      const entries = catSummaries.filter(cs => cs.category === cat.name)
      const totalWaste = entries.reduce((s, e) => s + (e.surplusValue || 0), 0)
      return { category: cat.label, waste: totalWaste }
    }).filter(w => w.waste > 0).sort((a, b) => b.waste - a.waste)

    const totalWaste = wasteByCategory.reduce((s, w) => s + w.waste, 0)
    const wastePct = stats.total > 0 ? (totalWaste / stats.total) * 100 : 0

    // Flags summary
    const highFlags = flags.filter(f => f.severity === 'HIGH').length
    const medFlags = flags.filter(f => f.severity === 'MEDIUM').length

    // Payday split (first half vs second half of month)
    const firstHalfTotal = summaries
      .filter(s => new Date(s.date + 'T00:00:00').getDate() <= 15)
      .reduce((sum, s) => sum + (s.totalSales || 0), 0)
    const secondHalfTotal = summaries
      .filter(s => new Date(s.date + 'T00:00:00').getDate() > 15)
      .reduce((sum, s) => sum + (s.totalSales || 0), 0)

    // Product breakdown
    const productSales = {}
    for (const s of sales) {
      const key = s.productName || s.productCode || 'Unknown'
      if (!productSales[key]) productSales[key] = { revenue: 0, qty: 0 }
      productSales[key].revenue += s.salesRands || 0
      productSales[key].qty += s.qtySold || 0
    }
    const topProducts = Object.entries(productSales)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10)

    // Recommendations
    const recommendations = []
    if (wastePct > 10) recommendations.push('Reduce opening stock for high-waste categories to lower surplus.')
    if (highFlags > 10) recommendations.push('Investigate HIGH flags — frequent pricing errors need attention.')
    if (stats.stdDev > stats.avg * 0.4 && tradingDays > 3) recommendations.push('Sales are highly variable — investigate inconsistent days.')
    if (firstHalfTotal > secondHalfTotal * 1.5 && mode === 'month') recommendations.push('Consider promotions in second half of month to balance revenue.')
    if (wasteByCategory.length > 0) recommendations.push(`Top waste category: ${wasteByCategory[0].category} — adjust ordering.`)

    return {
      summaries, stats, tradingDays, bestDay, worstDay,
      dowPerf, wasteByCategory, totalWaste, wastePct,
      highFlags, medFlags, firstHalfTotal, secondHalfTotal,
      recommendations, flags, sales, topProducts,
    }
  }, [currentDepartmentId, dateRange])

  const exportPdf = async () => {
    try {
      const { default: jsPDF } = await import('jspdf')
      await import('jspdf-autotable')

      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.width
      let y = 20

      doc.setFontSize(16)
      doc.text('DESRES KITCHEN AND CONFECTIONERY', pageWidth / 2, y, { align: 'center' })
      y += 8
      doc.setFontSize(12)
      doc.text(`${mode === 'day' ? 'Daily' : mode === 'week' ? 'Weekly' : 'Monthly'} Report — ${periodLabel}`, pageWidth / 2, y, { align: 'center' })
      y += 12

      doc.setFontSize(11)
      doc.setFont(undefined, 'bold')
      doc.text('1. Executive Summary', 14, y)
      y += 7
      doc.setFont(undefined, 'normal')
      doc.setFontSize(9)
      doc.text(`Trading Days: ${report.tradingDays}`, 14, y); y += 5
      doc.text(`Total Revenue: ${formatR(report.stats.total)}`, 14, y); y += 5
      doc.text(`Average Daily: ${formatR(report.stats.avg)}`, 14, y); y += 5
      if (report.bestDay) {
        doc.text(`Best Day: ${formatDate(report.bestDay.date)} (${formatR(report.bestDay.totalSales)})`, 14, y); y += 5
      }
      if (report.worstDay && report.tradingDays > 1) {
        doc.text(`Worst Day: ${formatDate(report.worstDay.date)} (${formatR(report.worstDay.totalSales)})`, 14, y); y += 5
      }
      doc.text(`Total Waste: ${formatR(report.totalWaste)} (${report.wastePct.toFixed(1)}%)`, 14, y); y += 5
      doc.text(`HIGH Flags: ${report.highFlags}`, 14, y); y += 10

      if (report.summaries.length > 0) {
        doc.setFontSize(11)
        doc.setFont(undefined, 'bold')
        doc.text('2. Revenue Summary', 14, y)
        y += 5

        doc.autoTable({
          startY: y,
          head: [['Date', 'Day', 'Sales (R)', 'Pap Qty', 'Flags']],
          body: report.summaries.map(s => [
            formatDate(s.date),
            s.dayOfWeek || '',
            formatR(s.totalSales),
            s.papQty || 0,
            (s.flagCountHigh || 0) + (s.flagCountMedium || 0),
          ]),
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [169, 60, 46] },
          theme: 'striped',
        })
        y = doc.lastAutoTable.finalY + 10
      }

      if (report.topProducts.length > 0) {
        if (y > 240) { doc.addPage(); y = 20 }
        doc.setFontSize(11)
        doc.setFont(undefined, 'bold')
        doc.text('3. Top Products', 14, y)
        y += 5

        doc.autoTable({
          startY: y,
          head: [['Product', 'Revenue (R)', 'Qty']],
          body: report.topProducts.map(p => [p.name, formatR(p.revenue), p.qty]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [169, 60, 46] },
          theme: 'striped',
        })
        y = doc.lastAutoTable.finalY + 10
      }

      if (report.recommendations.length > 0) {
        if (y > 240) { doc.addPage(); y = 20 }
        doc.setFontSize(11)
        doc.setFont(undefined, 'bold')
        doc.text('4. Operational Recommendations', 14, y)
        y += 7
        doc.setFont(undefined, 'normal')
        doc.setFontSize(9)
        for (const rec of report.recommendations) {
          doc.text(`• ${rec}`, 18, y)
          y += 5
        }
      }

      const filename = mode === 'day'
        ? `Desres_Report_${selectedDate}.pdf`
        : mode === 'week'
        ? `Desres_Report_Week_${dateRange.start}.pdf`
        : `Desres_Report_${year}_${String(month).padStart(2, '0')}.pdf`

      doc.save(filename)
      addToast('Report exported as PDF', 'success')
    } catch (err) {
      addToast('Failed to export PDF: ' + err.message, 'error')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-xl font-bold">Reports</h1>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Mode Selector */}
          <div className="flex bg-gray-800 rounded-lg p-0.5">
            {REPORT_MODES.map(m => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  mode === m.id ? 'bg-brand-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <m.icon size={14} />
                {m.label}
              </button>
            ))}
          </div>

          {/* Date Controls */}
          {mode === 'day' && (
            <DateInput value={selectedDate} onChange={setSelectedDate} />
          )}
          {mode === 'week' && (
            <DateInput value={selectedDate} onChange={setSelectedDate} />
          )}
          {mode === 'month' && (
            <>
              <select value={month} onChange={e => setMonth(Number(e.target.value))}
                className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2026, i).toLocaleString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select value={year} onChange={e => setYear(Number(e.target.value))}
                className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                <option value={2026}>2026</option>
                <option value={2025}>2025</option>
              </select>
            </>
          )}

          <button
            onClick={exportPdf}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm"
          >
            <FileDown size={16} /> Export PDF
          </button>
        </div>
      </div>

      {/* Period Label */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <FileText size={18} className="text-brand-400" />
          {mode === 'day' ? 'Daily' : mode === 'week' ? 'Weekly' : 'Monthly'} Report — {periodLabel}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-xs text-gray-400">Trading Days</p>
            <p className="text-xl font-bold">{report.tradingDays}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Total Revenue</p>
            <p className="text-xl font-bold text-green-400">{formatR(report.stats.total)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Avg Daily</p>
            <p className="text-xl font-bold">{formatR(report.stats.avg)}</p>
          </div>
          {report.bestDay && (
            <div>
              <p className="text-xs text-gray-400">Best Day</p>
              <p className="text-lg font-bold">{formatR(report.bestDay?.totalSales)}</p>
              <p className="text-xs text-gray-500">{formatDate(report.bestDay?.date)}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-400">Total Waste</p>
            <p className="text-lg font-bold text-amber-400">{formatR(report.totalWaste)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Waste %</p>
            <p className="text-lg font-bold">{report.wastePct.toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">HIGH Flags</p>
            <p className="text-lg font-bold text-red-400">{report.highFlags}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">MEDIUM Flags</p>
            <p className="text-lg font-bold text-amber-400">{report.medFlags}</p>
          </div>
        </div>

        {report.tradingDays === 0 && (
          <p className="text-gray-500 text-sm mt-4">No data for this period. Enter daily data first.</p>
        )}
      </div>

      {/* Top Products */}
      {report.topProducts.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Top Products</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2">#</th>
                <th className="text-left py-2">Product</th>
                <th className="text-right py-2">Revenue</th>
                <th className="text-right py-2">Qty</th>
              </tr>
            </thead>
            <tbody>
              {report.topProducts.map((p, i) => (
                <tr key={p.name} className="border-b border-gray-800/50">
                  <td className="py-1.5 text-gray-500">{i + 1}</td>
                  <td className="py-1.5">{p.name}</td>
                  <td className="py-1.5 text-right text-green-400">{formatR(p.revenue)}</td>
                  <td className="py-1.5 text-right text-gray-400">{p.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Day of Week Performance (week/month only) */}
      {report.dowPerf.length > 0 && mode !== 'day' && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Day-of-Week Performance</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2">Day</th>
                <th className="text-right py-2">Total</th>
                <th className="text-right py-2">Average</th>
                <th className="text-right py-2">Days</th>
              </tr>
            </thead>
            <tbody>
              {report.dowPerf.map(d => (
                <tr key={d.day} className="border-b border-gray-800/50">
                  <td className="py-2">{d.day}</td>
                  <td className="py-2 text-right">{formatR(d.total)}</td>
                  <td className="py-2 text-right text-green-400">{formatR(d.avg)}</td>
                  <td className="py-2 text-right text-gray-400">{d.days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payday Cycle (month only) */}
      {mode === 'month' && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Payday Cycle Analysis</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-400">1st — 15th</p>
              <p className="text-xl font-bold">{formatR(report.firstHalfTotal)}</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-400">16th — End</p>
              <p className="text-xl font-bold">{formatR(report.secondHalfTotal)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Waste */}
      {report.wasteByCategory.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Inventory & Waste Report</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2">Category</th>
                <th className="text-right py-2">Waste Value</th>
              </tr>
            </thead>
            <tbody>
              {report.wasteByCategory.map(w => (
                <tr key={w.category} className="border-b border-gray-800/50">
                  <td className="py-1.5">{w.category}</td>
                  <td className="py-1.5 text-right text-amber-400">{formatR(w.waste)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recommendations */}
      {report.recommendations.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Operational Recommendations</h3>
          <ul className="space-y-2">
            {report.recommendations.map((rec, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-brand-400 mt-0.5">•</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Daily Breakdown (week/month) */}
      {report.summaries.length > 0 && mode !== 'day' && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Daily Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-xs border-b border-gray-800">
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Day</th>
                  <th className="text-right py-2 px-2">Sales</th>
                  <th className="text-right py-2 px-2">Pap</th>
                  <th className="text-right py-2 px-2">Waste %</th>
                  <th className="text-right py-2 px-2">Flags</th>
                </tr>
              </thead>
              <tbody>
                {report.summaries.map(s => (
                  <tr key={s.date} className="border-b border-gray-800/50">
                    <td className="py-1.5 px-2">{formatDate(s.date)}</td>
                    <td className="py-1.5 px-2 text-gray-400">{s.dayOfWeek || ''}</td>
                    <td className="py-1.5 px-2 text-right">{formatR(s.totalSales)}</td>
                    <td className="py-1.5 px-2 text-right">{s.papQty || 0}</td>
                    <td className="py-1.5 px-2 text-right">{s.wastePct ? formatPct(s.wastePct) : '—'}</td>
                    <td className="py-1.5 px-2 text-right">{(s.flagCountHigh || 0) + (s.flagCountMedium || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
